import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Zap } from "lucide-react";
import { FadeUp, MaskedLine } from "../components/Reveal";
import { IMAGES, NEW_IMAGES } from "../site";

const FEATURED = {
  id: "nightwheels",
  src: NEW_IMAGES.nightWheels,
  title: "Night Moves",
  spec: "Wheel Lights · Undercarriage Illumination · Show Stance",
};

const BUILDS = [
  { id: "gmc", src: IMAGES.gmc, title: "GMC Denali", spec: "RGBW Underglow · Wheel Rings · Stance" },
  { id: "bluef250", src: NEW_IMAGES.blueF250, title: "F-450 Powerstroke", spec: "Elevate Suspension · Polished Forces" },
  { id: "tremor", src: NEW_IMAGES.tremor, title: "Super Duty Tremor", spec: "ETN Customz Livery · Show Stance" },
  { id: "chevy", src: IMAGES.chevy, title: "Chevy Z71", spec: "Lift Kit · Deep Lip Wheels · MT Tires" },
  { id: "blackford", src: NEW_IMAGES.blackFordRed, title: "F-250 Super Duty", spec: "Show Lift · Red Powder Coat" },
  { id: "ford", src: IMAGES.ford, title: "F-250 Super Duty", spec: "Wheel Lights · Grille Guard · Leveling" },
  { id: "denaliside", src: NEW_IMAGES.gmcLimeSide, title: "Sierra Denali HD", spec: "Lime Lift · Chrome Deep Lips" },
  { id: "engine", src: IMAGES.engine, title: "Duramax SDP Build", spec: "Engine Bay · Performance Piping" },
  { id: "f350", src: NEW_IMAGES.f350Shop, title: "Super Duty Tremor", spec: "ETN Customz Livery · Chrome Forces" },
  { id: "denalirear", src: NEW_IMAGES.gmcLimeRear, title: "Sierra Denali", spec: "Powder-Coated Hitch · Tail Lights" },
  { id: "denalidecal", src: NEW_IMAGES.denaliSide, title: "Sierra Denali HD", spec: "ETN Customz Livery · Purple Lift" },
  { id: "purplekit", src: NEW_IMAGES.purpleKit, title: "Purple Lift Kit", spec: "Fox Reservoir Shocks · Powder Coated" },
  { id: "jeeptailgate", src: NEW_IMAGES.jeepTailgate, title: "Jeep Gladiator", spec: "Custom Tailgate Badge · Mountain Overlay" },
  { id: "jeeprear", src: NEW_IMAGES.jeepRear, title: "Jeep Gladiator", spec: "Heavy-Duty Rear Bumper · Tow Hooks" },
  { id: "paintbadges", src: NEW_IMAGES.paintBadges, title: "F-450 Platinum Badges", spec: "Paint Matched · Blue Metallic" },
  { id: "painthandle", src: NEW_IMAGES.paintHandle, title: "Door Handle", spec: "Color Matched · Fresh Clear Coat" },
  { id: "paintmirror", src: NEW_IMAGES.paintMirror, title: "Mirror Cap", spec: "Color Matched · Gloss Finish" },
  { id: "paintrack", src: NEW_IMAGES.paintRack, title: "Tail Lights & Trim", spec: "Booth Session · Color Match" },
  { id: "paintbooth", src: NEW_IMAGES.paintBooth, title: "Full Trim Kit", spec: "Fresh Out the Booth" },
  { id: "logo", src: IMAGES.logo, title: "ETN Customz", spec: "Lifts · Lighting · Wheels · Tires · Accessories" },
];

const BuildCard = ({ build, index, onOpen }) => {
  const [pos, setPos] = useState({ x: "50%", y: "50%" });
  return (
    <FadeUp delay={(index % 3) * 0.08} className="mb-5 break-inside-avoid">
      <button
        type="button"
        onClick={() => onOpen(build)}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setPos({ x: `${e.clientX - r.left}px`, y: `${e.clientY - r.top}px` });
        }}
        data-testid={`gallery-item-${build.id}`}
        className="clip-corner hud-frame group relative block w-full overflow-hidden border border-zinc-800 text-left transition-colors duration-500 hover:border-bolt/60"
      >
        <img
          src={build.src}
          alt={`${build.title} — ${build.spec}`}
          loading="lazy"
          className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />
        <div
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{ background: `radial-gradient(300px at ${pos.x} ${pos.y}, rgba(168,85,247,0.22), transparent 70%)` }}
        />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <p className="font-display text-2xl sm:text-3xl font-bold uppercase italic tracking-tight text-white">
            {build.title}
          </p>
          <p className="mt-1 font-mono text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-volt">
            {build.spec}
          </p>
        </div>
      </button>
    </FadeUp>
  );
};

const FeaturedBuild = ({ onOpen }) => (
  <FadeUp>
    <button
      type="button"
      onClick={() => onOpen(FEATURED)}
      data-testid={`gallery-item-${FEATURED.id}`}
      className="clip-bevel hud-frame group relative block w-full overflow-hidden border border-zinc-800 text-left transition-colors duration-500 hover:border-volt/50"
    >
      <div className="relative aspect-[16/10] sm:aspect-[21/9]">
        <img
          src={FEATURED.src}
          alt={`${FEATURED.title} — ${FEATURED.spec}`}
          className="absolute inset-0 h-full w-full object-cover object-[center_60%] transition-transform duration-[1.2s] group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 p-6 sm:p-10">
          <p className="mb-2 flex items-center gap-2 font-mono text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-bolt-bright">
            <Zap size={13} fill="currentColor" /> Featured build
          </p>
          <p className="font-display text-4xl sm:text-6xl font-black uppercase italic tracking-tighter text-white">
            {FEATURED.title}
          </p>
          <p className="mt-2 font-mono text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-volt">
            {FEATURED.spec}
          </p>
        </div>
      </div>
    </button>
  </FadeUp>
);

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <main data-testid="gallery-page">
      <section className="relative overflow-hidden pt-36 sm:pt-44 pb-14 sm:pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-volt mb-4 flex items-center gap-2">
                <Zap size={13} fill="currentColor" className="text-bolt-bright" /> The Vault
              </p>
              <h1 className="font-display font-black uppercase italic tracking-tighter leading-[0.85]" data-testid="gallery-headline">
                <MaskedLine delay={0.15} className="text-5xl sm:text-7xl lg:text-8xl text-white">
                  Builds that
                </MaskedLine>
                <MaskedLine delay={0.3} className="text-5xl sm:text-7xl lg:text-8xl text-outline-volt">
                  Left the shop
                </MaskedLine>
              </h1>
            </div>
            <FadeUp delay={0.5}>
              <p className="max-w-xs text-sm leading-relaxed text-zinc-400 lg:text-right">
                Every photo is a truck that rolled out of ETN Customz. Tap any build for a closer look.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 pb-20 sm:pb-28">
        <FeaturedBuild onOpen={setActive} />
        <div className="mt-5 columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="gallery-grid">
          {BUILDS.map((b, i) => (
            <BuildCard key={b.id} build={b} index={i} onOpen={setActive} />
          ))}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/92 backdrop-blur-md p-4 sm:p-10"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <motion.figure
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 12 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="clip-corner relative max-h-full max-w-4xl overflow-hidden border border-zinc-700 bg-carbon"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.src} alt={active.title} className="max-h-[76vh] w-full object-contain bg-black" />
              <figcaption className="flex items-center justify-between gap-4 border-t border-zinc-800 px-5 py-4">
                <div>
                  <p className="font-display text-2xl font-bold uppercase italic text-white">{active.title}</p>
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-volt">{active.spec}</p>
                </div>
                <button
                  onClick={() => setActive(null)}
                  data-testid="gallery-lightbox-close"
                  aria-label="Close"
                  className="clip-badge flex h-10 w-10 items-center justify-center border border-zinc-600 text-zinc-300 transition-colors hover:border-bolt hover:text-bolt-bright"
                >
                  <X size={18} />
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
